import React from "react";
import { BigNumber } from "@ethersproject/bignumber";
import { LoadableEventLayout } from "pages/Event/components/LoadableEventLayout";
import { AboutSection } from "pages/Event/events/TradeToEarnEvent/components/AboutSection";
import { EpocheOverviewSection } from "pages/Event/events/TradeToEarnEvent/components/EpocheOverviewSection";
import {
  EpocheHistorySection,
  showOnlyEpochesWithRewardsAtom,
} from "pages/Event/events/TradeToEarnEvent/components/EpocheHistorySection";
import { useEventGeneralInfoQuery } from "pages/Event/events/TradeToEarnEvent/hooks/useEventGeneralInfoQuery";
import { Epoche, GeneralInfo, UserInfo } from "pages/Event/events/TradeToEarnEvent/types";
import { useEventUserInfoQuery } from "pages/Event/events/TradeToEarnEvent/hooks/useEventUserInfoQuery";
import { useAtom } from "jotai";
import { useEpochesHistoryQuery } from "./hooks/useEpochesHistoryQuery";
import trade2earnBackgroundLight from "img/T2EBackground.png";
import trade2earnBackgroundDark from "img/T2EBackgroundDark.png";

import { useUsers } from "./hooks/useUsers";
import bitcoinIcon from "components/TokenUtils/icons/dark/BTC.svg";
import etheruemIcon from "components/TokenUtils/icons/dark/ETH.svg";
import arbIcon from "img/lg_arbitrum.svg";
import solanaIcon from "components/TokenUtils/icons/dark/SOL.svg";
import { useMedia } from "react-use";
import { AnimatePresence, motion } from "framer-motion";

export type TierDetails = {
  tier: BigNumber;
  rewardPerToken: BigNumber;
  estimatedApr: BigNumber;
  totalRewards: BigNumber;
  startAmount: BigNumber;
  endAmount: BigNumber;
};

type TradeToEarnEventProps = {
  label: string;
  description?: string;
};

export const TradeToEarnEvent = (props: TradeToEarnEventProps) => {
  const eventGeneralInfoQuery = useEventGeneralInfoQuery();
  const eventUserInfoQuery = useEventUserInfoQuery();

  const isLoading = React.useMemo(() => {
    return !eventGeneralInfoQuery.data || !eventUserInfoQuery.data;
  }, [eventGeneralInfoQuery, eventUserInfoQuery]);

  return (
    <LoadableEventLayout isLoading={isLoading}>
      {eventGeneralInfoQuery.data && eventUserInfoQuery.data && (
        <TradeToEarnEventDummy
          {...props}
          generalInfo={eventGeneralInfoQuery.data}
          userInfo={eventUserInfoQuery.data}
        ></TradeToEarnEventDummy>
      )}
    </LoadableEventLayout>
  );
};

type TradeToEarnEventDummyProps = {
  label: string;
  description?: string;
  generalInfo: GeneralInfo;
  userInfo: UserInfo;
};

const TradeToEarnEventDummy = React.memo((props: TradeToEarnEventDummyProps) => {
  const { label, description, generalInfo, userInfo } = props;

  const epochesHistoryQuery = useEpochesHistoryQuery();

  const epoches = React.useMemo(() => {
    return epochesHistoryQuery.data?.endedEpoches ?? [];
  }, [epochesHistoryQuery.data]);

  const [showOnlyEpochesWithRewards] = useAtom(showOnlyEpochesWithRewardsAtom);

  const displayedEpoches = React.useMemo<Epoche[]>(() => {
    if (showOnlyEpochesWithRewards) {
      return epoches.filter((epoche) => epoche.rewardsPalm.gt(0));
    }
    return epoches;
  }, [epoches, showOnlyEpochesWithRewards]);

  const paidRewards = displayedEpoches.length * 500; // 500 tokens paid for each epoch

  const totalUsers = useUsers();

  const isMobile = useMedia("(max-width: 1100px)");

  return (
    <div>
      <div className="stars-bg absolute w-full h-full left-0"></div>
      <img
        src={trade2earnBackgroundLight}
        className="absolute top-[5.5rem] left-1/2 transform -translate-x-1/2 md:-translate-x-0  md:left-0 min-w-[200rem] md:min-w-full hidden dark:flex"
        alt=""
      />
      <img
        src={trade2earnBackgroundDark}
        className="absolute top-[5.5rem] left-1/2 transform -translate-x-1/2 md:-translate-x-0  md:left-0 min-w-[200rem] md:min-w-full dark:hidden flex"
        alt=""
      />
      <BluredEffect />
      {!isMobile && (
        <>
          <div className="absolute left-[20%] top-[15rem] h-[8.5rem] bg-gradient-to-t dark:from-white/20 dark:to-white/0 from-black/20 to-black/0 flex items-end">
            <img src={bitcoinIcon} alt="" className="w-[3.5rem] h-[3.5rem] mb-[-2rem]" />
          </div>
          <div className="absolute left-[30%] top-[20rem] h-[8.5rem] bg-gradient-to-t dark:from-white/20 dark:to-white/0 from-black/20 to-black/0 flex items-end">
            <img src={etheruemIcon} alt="" className="w-[3.5rem] h-[3.5rem] mb-[-2rem]" />
          </div>

          <div className="absolute right-[30%] top-[12rem] h-[8.5rem] bg-gradient-to-t dark:from-white/20 dark:to-white/0 from-black/20 to-black/0 flex items-end">
            <img src={arbIcon} alt="" className="w-[3.5rem] h-[3.5rem] mb-[-2rem]" />
          </div>

          <div className="absolute right-[20%] top-[23rem] h-[8.5rem] bg-gradient-to-t dark:from-white/20 dark:to-white/0 from-black/20 to-black/0 flex items-end">
            <img src={solanaIcon} alt="" className="w-[3.5rem] h-[3.5rem] mb-[-2rem]" />
          </div>
        </>
      )}

      <div className="tailwind isolate mb-16 flex flex-col items-stretch pt-[9.5rem]">
        <header>
          <div className="flex flex-col justify-between gap-10 items-start">
            <div className="flex flex-col gap-10 w-full items-center text-center">
              <h1 className="text-[5rem] leading-[1.4] font-medium">{label}</h1>

              <p className="text-[1.6rem] leading-[1.5] text-textColor">{description}</p>

              <div className="h-[3rem] text-main bg-[#F66B31]/10 flex items-center px-[1.5rem] rounded-[2rem] cursor-pointer">
                <p>How to participate on T2E event?</p>
              </div>
            </div>
            <div className="flex flex-row items-center w-full text-center mt-[3rem] hidden md:flex">
              <div className="flex flex-col gap-5 text-textColor flex-1">
                <p className="text-[1.6rem]">Users</p>
                <p className="text-[3.6rem]">{totalUsers.data?.users?.uniqueCountCumulative || 0}</p>
              </div>
              <div className="flex flex-col gap-5 text-textColor flex-1">
                <p className="text-[1.6rem]">Prize per Epoch</p>
                <p className="text-[3.6rem]">500 FOX</p>
              </div>
              <div className="flex flex-col gap-5 text-textColor flex-1">
                <p className="text-[1.6rem]">Paid Rewards</p>
                <p className="text-[3.6rem]">{paidRewards} FOX</p>
              </div>
            </div>
            <div className="flex flex-col items-center w-full text-center mt-[3rem] flex md:hidden">
              <div className="flex w-full border-b border-border ">
                <div className="flex flex-col gap-5 text-textColor flex-1 border-r border-border pb-[1.5rem]">
                  <p className="text-[1.6rem]">Users</p>
                  <p className="text-[3.6rem]">{totalUsers.data?.users?.uniqueCountCumulative || 0}</p>
                </div>
                <div className="flex flex-col gap-5 text-textColor flex-1 pb-[1.5rem]">
                  <p className="text-[1.6rem]">Prize per Epoch</p>
                  <p className="text-[3.6rem]">500 FOX</p>
                </div>
              </div>

              <div className="flex flex-col gap-5 text-textColor flex-1 mt-[2rem]">
                <p className="text-[1.6rem]">Paid Rewards</p>
                <p className="text-[3.6rem]">{paidRewards} FOX</p>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-col md:flex-row w-full gap-10">
          <AboutSection className="mt-[3rem]" generalInfo={generalInfo}></AboutSection>

          <EpocheOverviewSection
            className="mt-[3rem]"
            generalInfo={generalInfo}
            userInfo={userInfo}
          ></EpocheOverviewSection>
        </div>

        <EpocheHistorySection
          className="mt-[5rem]"
          generalInfo={generalInfo}
          userInfo={userInfo}
        ></EpocheHistorySection>
      </div>
    </div>
  );
});

const blurWrapperVariants = {
  hidden: {
    opacity: 0,
    transition: { duration: 0 },
  },
  visible: {
    opacity: 1,
    transition: { duration: 3 },
  },
};
type BluredEffectProps = {
  isHidden?: boolean;
};

const BluredEffect = React.memo((props: BluredEffectProps) => {
  return (
    <div className="tailwind">
      <AnimatePresence>
        {!props.isHidden && (
          <motion.div
            className="pointer-events-none"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={blurWrapperVariants}
          >
            <div className="@container/blurred-effect hidden md:flex">
              <div className=" h-[62rem] absolute inset-x-0 top-[30rem] mx-auto max-w-[185rem]">
                <div className="-inset-x-1/2 -top-2/3 absolute">
                  <div className="absolute left-1/2 -translate-x-1/2 -top-2/3 h-[72.2rem] rotate-[105deg] w-[35.2rem] bg-gradient-radial from-[rgba(245,_108,_49,_0.5)] to-[rgba(171,_171,_171,_0)] blur-[10rem]"></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
